import moment from "moment";
import React from "react";
import styled, { css } from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import { Divider } from "js/Components/Divider";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import { t } from "js/i18n";
import { PromoForm } from "js/react/views/Billing/Checkout/components/PromoForm";
import { SeatsSelect } from "js/react/views/Billing/Checkout/components/SeatsSelect";
import { formatPrice } from "js/react/views/Billing/utils/prices";

const SummaryCard = styled.div`
    background: #f7fafc;
    border: 1px solid #dae9f0;
    border-radius: 2px;
    padding: 20px;
    width: 100%;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SummaryTitle = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 10px;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #444444;
    min-width: 100%;
    width: min-content;
`;

const SummaryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
`;

const SummaryToggle = styled.button`
    background: none;
    border: none;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-weight: 600;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const DueTodayText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: 0.5px;
    color: #444444;
`;

const DueDateText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.25;
    color: #888888;
`;

const SeatsContainer = styled.div<{ $useMobileLayout?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    ${props => props.$useMobileLayout && css`
        flex-direction: row;
        justify-content: space-between;
    `}
`;

const DownArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <g clipPath="url(#clip0_68_229)">
            <path d="M4.63125 5.36865L7.5 8.23115L10.3688 5.36865L11.25 6.2499L7.5 9.9999L3.75 6.2499L4.63125 5.36865Z" fill="#11A9E2" />
        </g>
        <defs>
            <clipPath id="clip0_68_229">
                <rect width="15" height="15" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const UpArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <path d="M10.3688 9.63135L7.5 6.76885L4.63125 9.63135L3.75 8.7501L7.5 5.0001L11.25 8.7501L10.3688 9.63135Z" fill="#11A9E2" />
    </svg>
);

export const PlanSummary = CheckoutController.withState(
    function PlanSummary({ expandable, tier }: { expandable?: boolean, tier: WorkspacePlanTier } & CheckoutControllerState) {
        const [expanded, setExpanded] = React.useState(false);

        const isTrial = CheckoutController.availableTrialDays > 0;
        const billingInterval = CheckoutController.billingInterval;
        const productDisplayName = CheckoutController.productDisplayName;
        const isAnnual = billingInterval === "year";
        const checkoutType = CheckoutController.checkoutType;

        const {
            dueNowTax,
            dueNextTax,
            dueNext,
            dueNow,
            total,
            annualCostAtMonthlyRate,
            annualCost,
            prorationCredit
        } = CheckoutController.amounts;

        const trialExpirationDate = moment().add(CheckoutController.availableTrialDays, "days").format("MMMM Do, YYYY");
        const nextBillingDate = isTrial ? trialExpirationDate : isAnnual
            ? moment().add(1, "year").format("MMMM Do, YYYY")
            : moment().add(1, "month").format("MMMM Do, YYYY");

        const dueNowTaxString = dueNowTax > 0 ? t(" plus tax") : "";
        const dueNextTaxString = dueNextTax > 0 ? t(" plus tax") : "";

        const showSeatsSelect = tier === WorkspacePlanTier.TEAM;

        const currency = CheckoutController.currency;

        if (expandable) {
            return (
                <FlexLayout direction="column" gap={10} width="fill" style={{ overflowY: "visible", overflowX: "visible" }}>
                    {showSeatsSelect && (<>
                        <SeatsContainer $useMobileLayout>
                            <TextContent center>Seats</TextContent>
                            <SeatsSelect disabled={checkoutType === "reactivate"} />
                        </SeatsContainer>
                        <Divider margin={15} />
                    </>)}
                    {expanded &&
                        <FlexLayout direction="column" gap={5} padding="0 10px">
                            <FlexLayout justify="space-between">
                                <TextContent helper>{t("{{productDisplayName}} - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                                <TextContent helper>{formatPrice(total, currency)}/{t(billingInterval)}</TextContent>
                            </FlexLayout>
                            <FlexLayout justify="space-between">
                                <TextContent helper>{t("{{price}} x {{count}} {{unit}}", { price: formatPrice(isAnnual ? (total / 12) : total, currency), count: isAnnual ? 12 : 1, unit: t(isAnnual ? "months" : "month") })}</TextContent>
                                <TextContent helper>{formatPrice(total, currency)}</TextContent>
                            </FlexLayout>
                            {prorationCredit > 0 && (
                                <FlexLayout justify="space-between">
                                    <TextContent helper>{t("Proration credit")}</TextContent>
                                    <TextContent helper>{formatPrice(-prorationCredit, currency)}</TextContent>
                                </FlexLayout>
                            )}
                            <FlexLayout justify="space-between">
                                <TextContent helper>{t("Tax")}</TextContent>
                                <TextContent helper>{dueNowTax > 0 ? t("+ {{amount}}", { amount: formatPrice(dueNowTax, currency) }) : t("-")}</TextContent>
                            </FlexLayout>
                            <Divider margin={10} />
                            <FlexLayout justify="space-between">
                                <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueNow, currency) })}{dueNow > 0 ? dueNowTaxString : ""}</DueTodayText>
                                <SummaryToggle onClick={() => setExpanded(false)}>
                                    {t("HIDE SUMMARY")}
                                    <UpArrowIcon />
                                </SummaryToggle>
                            </FlexLayout>
                            <FlexLayout justify="space-between" align="center">
                                <DueDateText>{t("Due {{date}}: {{amount}}", { date: nextBillingDate, amount: formatPrice(dueNext, currency) })}{dueNextTaxString}</DueDateText>
                            </FlexLayout>
                        </FlexLayout>
                    }
                    {!expanded &&
                        <FlexLayout direction="column" gap={5} padding="0 10px">
                            <FlexLayout justify="space-between" align="center">
                                <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueNow, currency) })}{dueNow > 0 ? dueNowTaxString : ""}</DueTodayText>
                                <SummaryToggle onClick={() => setExpanded(true)}>
                                    {t("VIEW SUMMARY")}
                                    <DownArrowIcon />
                                </SummaryToggle>
                            </FlexLayout>
                            <FlexLayout justify="space-between" align="center">
                                <DueDateText>{t("Due {{date}}: {{amount}}", { date: nextBillingDate, amount: formatPrice(dueNext, currency) })}{dueNextTaxString}</DueDateText>
                            </FlexLayout>
                        </FlexLayout>
                    }
                </FlexLayout>
            );
        }

        return (
            <SummaryCard>
                <SummaryTitle>{t("Summary")}</SummaryTitle>
                <SummaryContent>
                    <FlexLayout width="fill" direction="column" gap={5}>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("{{productDisplayName}} - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                            <TextContent helper>{formatPrice(total, currency)} / {t(billingInterval)}</TextContent>
                        </FlexLayout>
                    </FlexLayout>
                    <Divider />
                    {showSeatsSelect && (<>
                        <SeatsContainer>
                            <TextContent>Seats</TextContent>
                            <SeatsSelect disabled={checkoutType === "reactivate"}
                            />
                        </SeatsContainer>
                        <Divider />
                    </>)}
                    <FlexLayout width="fill" direction="column" gap={5}>
                        <FlexLayout justify="space-between">
                            <TextContent helper>
                                {isAnnual
                                    ? t("{{price}} x {{count}} months", { price: formatPrice(annualCost / 12, currency), count: 12 })
                                    : t("{{price}} x {{count}} month", { price: formatPrice(annualCostAtMonthlyRate / 12, currency), count: 1 })}
                            </TextContent>
                            <TextContent helper><span data-testid="plan-summary-subtotal">{formatPrice(total, currency)}</span></TextContent>
                        </FlexLayout>
                        {prorationCredit > 0 && (
                            <FlexLayout justify="space-between">
                                <TextContent helper>{t("Proration credit")}</TextContent>
                                <TextContent helper>{formatPrice(-prorationCredit, currency)}</TextContent>
                            </FlexLayout>
                        )}
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("Tax")}</TextContent>
                            <TextContent helper>{dueNowTax > 0 ? t("+ {{amount}}", { amount: formatPrice(dueNowTax, currency) }) : t("-")}</TextContent>
                        </FlexLayout>
                    </FlexLayout>
                    <Divider />
                    <FlexLayout width="fill" direction="column" gap={5}>
                        <FlexLayout justify="space-between">
                            <TextContent sectionLabel>{t("Due Today")}</TextContent>
                            <TextContent sectionLabel>{formatPrice(dueNow, currency)}{dueNow > 0 ? dueNowTaxString : ""}</TextContent>
                        </FlexLayout>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("Due {{date}}", { date: nextBillingDate })}</TextContent>
                            <TextContent helper>{formatPrice(dueNext, currency)}{dueNext > 0 ? dueNextTaxString : ""}</TextContent>
                        </FlexLayout>
                    </FlexLayout>
                    <FlexLayout width="fill" justify="center">
                        <PromoForm />
                    </FlexLayout>
                </SummaryContent>
            </SummaryCard>
        );
    }
);
